<template>
  <SubmenuMainContainer v-model:currentMenu="currentMenu" :menus="menus">
    <template #[SectionKey.Plan]>
      <PlansTemplate @emitSlotEvent="handleSlotEvent" />
    </template>
    <template #[SectionKey.RechargeBill]>
      <RechargeTemplate @emitSlotEvent="handleSlotEvent" />
    </template>
    <template #[SectionKey.SlotsInfo]>
      <SlotsTemplate @emitSlotEvent="handleSlotEvent" />
    </template>

    <template #[TabKey.VoiceWallet]>
      <VoiceBillingTemplate :dataPlan="dataPlan" />
    </template>

    <template #[TabKey.WhatsappWallet]>
      <BillingTemplate :dataPlan="dataPlan" />
    </template>

    <template #[TabKey.WhatsappVoiceWallet]>
      <WhatsappVoiceTemplate />
    </template>

    <template #[TabKey.SmsWallet]>
      <SmsBillingTemplate :dataPlan="dataPlan" />
    </template>

    <template #[TabKey.TextToSpeechWallet]>
      <TextToSpeechBillingTemplate :dataPlan="dataPlan" />
    </template>

    <template #[TabKey.ApiCallsWallet]>
      <ApiCallsBillingTemplate />
    </template>

    <!-- <template #[TabKey.BotAI_MessageWallet]>
      <BotAIBillingTemplate />
    </template> -->
  </SubmenuMainContainer>

  <RestrictionChangePlanModal
    :title="
      isClientBeexcc
        ? 'Realiza un upgrade y accede a todo el poder de Beex.'
        : 'Solicita un upgrade y accede a todo el poder de BeexCC.'
    "
    v-model:open="openRestriction"
  >
    <template #description>
      Tu plan {{ PlanTypeText[plan as PlanType] }} no incluye el canal de <span class="bold">{{ channelTab }}</span>.
      Solicita un upgrade y potencia tu ambiente de trabajo.
      ¡Que tu viaje no se detenga!
    </template>
  </RestrictionChangePlanModal>
</template>

<script setup lang="ts">
import { computed, provide, ref, watch } from "vue";

import { SubmenuData } from "@/@types/shared/pickers";

import SubmenuMainContainer from "@/app/shared/components/organisms/SubmenuMainContainer.vue";
import RechargeTemplate from "@/app/settings/components/templates/RechargeTemplate.vue";
import SlotsTemplate from "../components/templates/SlotsTemplate.vue";
import BillingTemplate from "@/app/settings/components/templates/BillingTemplate.vue";
import WhatsappVoiceTemplate from "@/app/settings/components/templates/WhatsappVoiceTemplate.vue";
import VoiceBillingTemplate from "../components/templates/VoiceBillingTemplate.vue";
import SmsBillingTemplate from "@/app/settings/components/templates/SmsBillingTemplate.vue";
import ApiCallsBillingTemplate from "@/app/settings/components/templates/ApiCallsBillingTemplate.vue";
import TextToSpeechBillingTemplate from "@/app/settings/components/templates/TextToSpeechBillingTemplate.vue";
import BotAIBillingTemplate from "@/app/settings/components/templates/BotAIBillingTemplate.vue";
import PlansTemplate from "../components/templates/PlansTemplate.vue";
import { useStore } from "vuex";
import { ChannelPlan, ChannelPlanType, PlanType, PlanTypeText } from "@/app/common/types";
import RestrictionChangePlanModal from "@/app/common/components/modals/RestrictionChangePlanModal.vue";
import { ProductType } from "@/@types/settings/billing";

const PlanSpecial = 5;

// const tabs: TabsData[] = [
//   {
//     key: "voice",
//     label: "Llamadas",
//   },
//   {
//     key: "whatsapp",
//     label: "WhatsApp",
//   },
// ];

enum MenuKey {
  Plan = "plan",
  Billing = "billing",
  Slots = "slots",
  Recharges = "recharges",
}

enum SectionKey {
  Plan = "plan",
  RechargeBill = "recharge-bill",
  SlotsInfo = "slots-info",
  RechargeExpenses = "recharge-expenses",
}

enum TabKey {
  VoiceWallet = "voice-wallet",
  WhatsappWallet = "whatsapp-wallet",
  WhatsappVoiceWallet = "whatsapp-voice-wallet",
  SmsWallet = "sms-wallet",
  BotAI_MessageWallet = "botai-message-wallet",
  TextToSpeechWallet = "text-to-speech-wallet",
  ApiCallsWallet = "api-calls-wallet",
}

const allMenus: SubmenuData<MenuKey, SectionKey, TabKey>[] = [
  {
    name: "Mi plan",
    key: MenuKey.Plan,
    wholePage: true,
    sections: [
      {
        title: "Mi plan",
        key: SectionKey.Plan,
      },
    ],
  },
  {
    name: "Facturación",
    key: MenuKey.Billing,
    wholePage: true,
    sections: [
      {
        title: "Facturación",
        key: SectionKey.RechargeBill,
        tag: {
          label: "Slots",
          tooltip: "Tu facturación actual es por la cantidad de slots que manejes.",
        },
      },
    ],
  },
  {
    name: "Slots",
    key: MenuKey.Slots,
    wholePage: true,
    sections: [
      {
        title: "Slots",
        key: SectionKey.SlotsInfo,
      },
    ],
  },
  {
    name: "Recargas y gastos",
    key: MenuKey.Recharges,
    tabs: [
      {
        key: TabKey.VoiceWallet,
        label: "Llamadas",
      },
      {
        key: TabKey.WhatsappWallet,
        label: "WhatsApp Message",
      },
      {
        key: TabKey.WhatsappVoiceWallet,
        label: "WhatsApp Voice",
      },
      {
        key: TabKey.SmsWallet,
        label: "SMS",
      },
      {
        key: TabKey.TextToSpeechWallet,
        label: "Text To Speech",
      },
      {
        key: TabKey.ApiCallsWallet,
        label: "API Calls",
      },
      // {
      //   key: TabKey.BotAI_MessageWallet,
      //   label: "BotAI Message",
      // },
    ],
  },
];

const currentMenu = ref<MenuKey | TabKey>(MenuKey.Billing);

const channelTab = ref("");

const store = useStore();
const user = computed(() => store.getters["getDataUser"]);
const conditionalMenus: MenuKey[] = [MenuKey.Plan, MenuKey.Billing, MenuKey.Slots];
const conditionalTabs: TabKey[] = [TabKey.WhatsappVoiceWallet];
const isClientBeexcc = computed(() => user.value?.is_beexcc);
const menus = computed(() => {
  return allMenus
    .filter((menu) => {
      if (conditionalMenus.includes(menu.key)) return isClientBeexcc.value;
      return true;
    })
    .map((menu) => {
      if (!menu.tabs?.length) return menu;

      const tabs = menu.tabs.filter((tab) => {
        if (conditionalTabs.includes(tab.key)) return isClientBeexcc.value;
        if (tab.key === TabKey.TextToSpeechWallet) return accessIvr.value;
        // El tab de API Calls se muestra en todos los planes: es el punto donde el cliente
        // ve su consumo y, si el plan no incluye la funcionalidad, el tag Upgrade/Personalizar.
        return true;
      });

      if (tabs.length === menu.tabs.length) return menu;
      return { ...menu, tabs };
    });
});
const dataPlan = computed(() => store.getters["getDataPlanDetails"]);
const openRestriction = ref(false);

const plan = computed(() => {
  return user.value?.change_custom_plan?.plan;
});

const haveIvrPlan = computed(() => {
  return dataPlan.value?.configuration?.ivr_outbound?.active &&
    dataPlan.value?.configuration?.ivr_outbound?.checked;
});

const accessIvr = computed(() => {
  return [PlanType.AllInOne, PlanSpecial].includes(plan.value) ||
    (
      [PlanType.Voice, PlanType.DigitalVoice].includes(plan.value) &&
      haveIvrPlan.value
    );
});

const channelsConfig = computed(() => {
  console.log("user.value?.change_custom_plan?.channels:", user.value?.change_custom_plan?.channels);
  return user.value?.change_custom_plan?.channels || [];
});

const hasSms = computed(() => {
  if (!channelsConfig.value) return false;
  return channelsConfig.value[ChannelPlan[ChannelPlanType.Sms]];
});
const hasWhatsapp = computed(() => {
  if (!channelsConfig.value) return false;
  return channelsConfig.value[ChannelPlan[ChannelPlanType.WhatsApp]];
});
const hasVoice = computed(() => {
  if (!channelsConfig.value) return false;
  return channelsConfig.value[ChannelPlan[ChannelPlanType.Telefonia]];
});
provide("hasSms", hasSms);
provide("hasWhatsapp", hasWhatsapp);
provide("hasVoice", hasVoice);

const handleOpenRestriction = (type: ProductType) => {
  console.log("handleOpenRestriction called with type:", type);
  switch (type) {
    case ProductType.Voice:
      channelTab.value = "Voz";
      break;
    case ProductType.WhatsApp:
      channelTab.value = "WhatsApp";
      break;
    case ProductType.Sms:
      channelTab.value = "SMS";
      break;
    default:
      channelTab.value = "";
  }
  openRestriction.value = true;
};
provide("handleOpenRestriction", handleOpenRestriction);

watch(
  menus,
  (menuList) => {
    const isCurrentMenuVisible = menuList.some((menu) => menu.key === currentMenu.value);
    if (isCurrentMenuVisible) return;

    currentMenu.value = menuList[0]?.key || MenuKey.Recharges;
  },
  { immediate: true }
);

const handleSlotEvent = (message: string) => {
  if (!message) return;
  const menuSelected = menus.value.find((menu) => menu.key === message);
  console.log(menuSelected, "menuSelected");

  if (menuSelected) {
    currentMenu.value = menuSelected.key;
  }
};
</script>

<style lang="sass" scoped>
section
  padding: 0
.page
  display: flex
  height: 100%

.menu
  margin: 12px 0 !important
  padding-top: 10px !important
  border: 1px solid $gray-5 !important
  border-left: none !important
  border-radius: 0 10px 10px 0
  flex: 0 0 220px

.main
  flex: 1 1 auto
  padding: 20px

  .title
    font-size: 20px
    font-weight: 600
    line-height: 20px
    margin: 0
.divider
  margin: 20px 0
  background-color: $gray-4

:deep(.section)
  padding: 0

:deep(.main .title)
  padding: 0

.bold
  font-weight: 600
</style>
